import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import StyledAlert from "./components/StyledAlert";
import { ThemeContext } from "./App";

const InvestmentPlans = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertCallback, setAlertCallback] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setAlertMessage("User not logged in!");
      setAlertCallback(() => () => navigate("login.html"));
    }
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await fetch("https://tradespots.online/api/investments/plans");
      const data = await response.json();
      const updatedPlans = data.map((plan) => ({
        ...plan,
        calculatedEarnings: parseFloat((plan.dailyEarnings * 60).toFixed(3)), // Calculate total earnings for 60 days
      }));
      setPlans(updatedPlans);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchInvestmentHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");
      const response = await fetch(
        `https://tradespots.online/api/investments/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch investments");
      const investmentsData = await response.json();
      const investments = Array.isArray(investmentsData)
        ? investmentsData
        : investmentsData.investments || [];
      const historyInvestments = investments.filter(
        (investment) =>
          investment.status === "completed" || investment.status === "cancelled"
      );
      setInvestmentHistory(historyInvestments);
    } catch (error) {
      console.error("Error fetching investment history:", error);
      setAlertMessage("Failed to load investment history.");
    } finally {
      setLoading(false);
    }
  };

  const invest = async (planId) => {
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authToken");
    if (!userId || !authToken) {
      setAlertMessage("You are not logged in!");
      setAlertCallback(() => () => (window.location.href = "login.html"));
      return;
    }
    try {
      const response = await fetch(
        "https://tradespots.online/api/investments/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ userId, planId }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Investment failed");
      setAlertMessage(`Investment successful: ${data.planName || planId}`);
    } catch (error) {
      setAlertMessage("Error: " + error.message);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', sans-serif",
        background:
          theme === "dark"
            ? "linear-gradient(135deg, #232526 0%, #414345 100%)"
            : "linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)",
        color: theme === "dark" ? "#f1f1f1" : "#333",
        padding: "20px",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* StyledAlert */}
      {alertMessage && (
        <StyledAlert
          message={alertMessage}
          onClose={() => {
            setAlertMessage("");
            setAlertCallback(null);
          }}
          onConfirm={alertCallback}
        />
      )}
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          position: "absolute",
          top: "30px",
          left: "10px",
          fontSize: "10px",
          backgroundColor: theme === "dark" ? "#1e88e5" : "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          padding: "8px 14px",
          boxShadow:
            theme === "dark"
              ? "0 4px 6px rgba(255,255,255,0.15)"
              : "0 4px 6px rgba(0, 0, 0, 0.2)",
          transition: "background-color 0.3s ease, transform 0.3s ease",
        }}
      >
        🔙 Back
      </button>
      <button
        onClick={() => {
          fetchInvestmentHistory();
          setModalVisible(true);
        }}
        style={{
          position: "absolute",
          top: "30px",
          right: "10px",
          fontSize: "10px",
          backgroundColor: theme === "dark" ? "#1e88e5" : "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          padding: "8px 14px",
          boxShadow:
            theme === "dark"
              ? "0 4px 6px rgba(255,255,255,0.15)"
              : "0 4px 6px rgba(0, 0, 0, 0.2)",
          transition: "background-color 0.3s ease, transform 0.3s ease",
        }}
      >
        📜 History
      </button>
      <div
        style={{
          background: theme === "dark" ? "#232323cc" : "#ffffffcc",
          padding: "30px 20px 40px 20px",
          borderRadius: "18px",
          maxWidth: "1100px",
          margin: "0 auto",
          boxShadow:
            theme === "dark"
              ? "0 8px 32px rgba(30,136,229,0.15)"
              : "0 8px 32px rgba(0, 0, 0, 0.10)",
          borderTop: `4px solid ${theme === "dark" ? "#1e88e5" : "#007bff"}`,
        }}
      >
        <h1
          style={{
            marginBottom: "24px",
            color: theme === "dark" ? "#f1f1f1" : "#333",
            fontSize: "28px",
            textAlign: "center",
            borderBottom: `2px solid ${theme === "dark" ? "#1e88e5" : "#007bff"}`,
            paddingBottom: "12px",
            letterSpacing: 1.2,
          }}
        >
         TradeSpot Market
        </h1>
        {plansLoading ? (
          <div
            style={{
              textAlign: "center",
              fontSize: 18,
              margin: 40,
              color: theme === "dark" ? "#b0c4de" : "#555",
            }}
          >
            Loading plans...
          </div>
        ) : plans.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              fontSize: 16,
              margin: 40,
              color: theme === "dark" ? "#b0c4de" : "#555",
            }}
          >
            No plans available at the moment.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "28px",
              marginTop: "20px",
            }}
          >
            {plans.map((plan) => {
              return (
                <div
                  key={plan._id}
                  style={{
                    background: theme === "dark" ? "#181c24" : "#f7fafd",
                    padding: "12px 8px 10px 8px", // much smaller padding
                    borderRadius: "10px",
                    border: theme === "dark" ? "1px solid #444" : "1px solid #e0e0e0",
                    boxShadow:
                      theme === "dark"
                        ? "0 1px 4px rgba(30,136,229,0.10)"
                        : "0 1px 4px rgba(0,0,0,0.07)",
                    width: "120px", // much smaller width
                    minHeight: "110px", // much smaller height
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    position: "relative",
                    margin: "4px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: theme === "dark" ? "#1e88e5" : "#007bff",
                      textAlign: "center",
                      width: "100%",
                      marginBottom: 2,
                    }}
                  >
                    {plan.name}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme === "dark" ? "#fff" : "#222",
                      textAlign: "center",
                      margin: "6px 0 2px 0",
                    }}
                  >
                    {plan.amount} SPOT
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: theme === "dark" ? "#b0c4de" : "#555",
                      textAlign: "center",
                      marginBottom: 6,
                    }}
                  >
                  Total Earnings for 60 days:{" "}
                  <span
                    style={{
                      color: theme === "dark" ? "#1e88e5" : "#007bff",
                      fontWeight: 600,
                    }}
                  >
                    {plan.calculatedEarnings} SPOT
                  </span>
                  </div>
                  <button
                    onClick={() => invest(plan._id)}
                    style={{
                      background:
                        theme === "dark"
                          ? "linear-gradient(90deg,#1e88e5,#1976d2)"
                          : "linear-gradient(90deg,#007bff,#00bfff)",
                      color: "#fff",
                      padding: "6px 0px",
                      width: "90%",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 12,
                      marginTop: 4,
                      boxShadow:
                        theme === "dark"
                          ? "0 1px 3px rgba(30,136,229,0.10)"
                          : "0 1px 3px rgba(0,0,0,0.10)",
                      transition: "background 0.3s, transform 0.2s",
                    }}
                  >
                    Buy Now
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {modalVisible && (
        <div
          style={{
            display: "flex",
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            zIndex: "1000",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: theme === "dark" ? "#232323" : "#fff",
              color: theme === "dark" ? "#f1f1f1" : "#333",
              padding: "20px",
              borderRadius: "10px",
              maxWidth: "600px",
              maxHeight: "80%",
              overflowY: "auto",
              borderTop: `4px solid ${theme === "dark" ? "#1e88e5" : "#007bff"}`,
              boxShadow:
                theme === "dark"
                  ? "0 8px 20px rgba(255,255,255,0.15)"
                  : "0 8px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h2
              style={{
                margin: "0 0 10px",
                color: theme === "dark" ? "#1e88e5" : "#007bff",
              }}
            >
              Investment History
            </h2>
            <div>
              {loading ? (
                <p>Loading...</p>
              ) : investmentHistory.length > 0 ? (
                investmentHistory.map((investment, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "10px",
                      padding: "10px",
                      background: theme === "dark" ? "#181818" : "#f9f9f9",
                      borderLeft: `4px solid ${theme === "dark" ? "#1e88e5" : "#007bff"}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                      color: theme === "dark" ? "#f1f1f1" : "#333",
                    }}
                  >
                    <p>
                      <strong>Plan:</strong> {investment.plan}
                    </p>
                    <p>
                      <strong>Amount:</strong> {investment.amount} SPOT
                    </p>
                    <p>
                      <strong>Status:</strong> {investment.status}
                    </p>
                    <p>
                      <strong>End Date:</strong>{" "}
                      {new Date(investment.endDate).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                ))
              ) : (
                <p style={{ textAlign: "center", fontSize: "14px" }}>
                  No completed or cancelled investments.
                </p>
              )}
            </div>
            <button
              onClick={() => setModalVisible(false)}
              style={{
                backgroundColor: theme === "dark" ? "#1e88e5" : "#007bff",
                color: "#fff",
                padding: "8px 12px",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "transform 0.3s ease",
                marginTop: "15px",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentPlans;